import {
  CheckCircle2,
  Copy,
  Edit,
  Send,
} from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/Accordion";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { InputGroup } from "@/components/ui/Input";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Textarea } from "@/components/ui/Textarea";
import { useAuthStore } from "@/store/auth.store";
import { userCan } from "@/lib/permissions";

// SCH-07 (S-171): one review decision row from GET /schemas/:id/versions/:vId/reviews
interface VersionReviewRow {
  id: string;
  decision: "APPROVED" | "REJECTED";
  comment: string | null;
  reviewerId: string;
  reviewerName: string | null;
  createdAt: string;
}
import { ContextBanner } from "@/components/common/ContextBanner";
import { DataTable } from "@/components/common/DataTable";
import type { ColumnDef } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { toast } from "@/components/ui/Toast";
import { api } from "@/lib/api-client";
import { formatDate } from "@/lib/format";
import { useTranslation } from "@/hooks/useTranslation";
import type { SchemaVersionStatus } from "@/types";

// ─────────────────────────────────────────────
// SchemaDetail — Screen 8 (dKtxg)
//
// Schema version with domains accordion, postures,
// questions table, scoring config, and action buttons.
// Design: §8.5 Screen 8
// ─────────────────────────────────────────────

// ── Types ─────────────────────────────────────

type QuestionType =
  | "WEIGHTED_MULTIPLE_CHOICE"
  | "WEIGHTED_YES_NO"
  | "MULTI_SUB_FIELD"
  | "OPEN_ENDED";

interface Question {
  id: string;
  code: string;
  text: string;
  type: QuestionType;
  weight?: number;
  required?: boolean;
  domainId: string;
  postureId: string;
  controlMappings?: unknown[];
  conditionalRules?: unknown[];
}

// Postures are returned flat alongside domains; questions link them via domainId
interface Posture {
  id: string;
  code: string;
  name: string;
  weight?: number;
  sortOrder?: number;
  domainId?: string | null;
  questions: Question[];
}

// Domains are flat — no nested postures in the API response
interface Domain {
  id: string;
  code: string;
  name: string;
  sortOrder?: number;
}

// Parent schema record (from GET /schemas/:id)
interface SchemaRecord {
  id: string;
  name: string;
  type: string;
  status: string;
  isPreset?: boolean;
}

interface ScoringConfig {
  defaultPresetKey?: string;
  unansweredPolicy?: string;
  subFieldAggregation?: string;
}

// What GET /schemas/:id/versions/:vId actually returns
interface SchemaVersionDetail {
  id: string;
  schemaId: string;
  version: number;
  status: SchemaVersionStatus;
  changelog?: string | null;
  createdAt: string;
  publishedAt?: string | null;
  scoringConfig?: ScoringConfig;
  // Flat arrays — postures are siblings of domains, NOT nested
  domains: Domain[];
  postures: Posture[];
}

// Derived shape used by the UI: domains with their postures grouped client-side
interface DomainWithPostures extends Domain {
  postures: Posture[];
  questionCount: number;
}

// ── Question type badge ───────────────────────

function QuestionTypeBadge({ type }: { type: QuestionType }) {
  const { t } = useTranslation();
  const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
    WEIGHTED_MULTIPLE_CHOICE: t("schema.questionTypes.WEIGHTED_MULTIPLE_CHOICE"),
    WEIGHTED_YES_NO: t("schema.questionTypes.WEIGHTED_YES_NO"),
    MULTI_SUB_FIELD: t("schema.questionTypes.MULTI_SUB_FIELD"),
    OPEN_ENDED: t("schema.questionTypes.OPEN_ENDED"),
  };
  return (
    <span className="inline-flex items-center rounded-badge bg-hover px-2 py-0.5 text-[11px] font-semibold leading-none text-text-muted">
      {QUESTION_TYPE_LABELS[type] ?? type}
    </span>
  );
}

// ── Question table columns ────────────────────

function buildQuestionColumns(t: (key: string) => string): ColumnDef<Question>[] {
  return [
    {
      key: "code",
      header: t("schema.questionTable.code"),
      cell: (row) => (
        <span className="font-mono text-caption text-text-muted">{row.code}</span>
      ),
    },
    {
      key: "text",
      header: t("schema.questionTable.question"),
      cell: (row) => (
        <span className="line-clamp-2 max-w-sm text-body text-text-secondary">
          {row.text}
        </span>
      ),
    },
    {
      key: "type",
      header: t("entity.type"),
      cell: (row) => <QuestionTypeBadge type={row.type} />,
    },
    {
      key: "weight",
      header: t("schema.questionTable.weight"),
      cell: (row) => (
        <span className="text-body text-text-secondary">
          {row.weight ?? "-"}
        </span>
      ),
    },
    {
      key: "required",
      header: t("common.required"),
      cell: (row) =>
        row.required ? (
          <CheckCircle2 className="h-4 w-4 text-success" />
        ) : (
          <span className="text-text-muted">-</span>
        ),
    },
    {
      key: "controlMappings",
      header: t("schema.questionTable.controls"),
      cell: (row) => (
        <span className="text-body text-text-secondary">
          {row.controlMappings?.length ?? 0}
        </span>
      ),
    },
  ];
}

// ── Scoring config display ────────────────────

// Known enum values → label keys; unknown values fall back to the raw string.
const UNANSWERED_POLICY_LABEL_KEYS: Record<string, string> = {
  EXCLUDE: "schema.scoringConfig.unansweredPolicyValues.EXCLUDE",
  ZERO: "schema.scoringConfig.unansweredPolicyValues.ZERO",
  MINIMUM: "schema.scoringConfig.unansweredPolicyValues.MINIMUM",
};

const SUB_FIELD_AGGREGATION_LABEL_KEYS: Record<string, string> = {
  AVERAGE: "schema.scoringConfig.subFieldAggregationValues.AVERAGE",
  MAX: "schema.scoringConfig.subFieldAggregationValues.MAX",
  WEIGHTED: "schema.scoringConfig.subFieldAggregationValues.WEIGHTED",
};

function ScoringConfigCard({ config }: { config?: ScoringConfig }) {
  const { t } = useTranslation();
  if (!config) return null;
  const policyKey = config.unansweredPolicy
    ? UNANSWERED_POLICY_LABEL_KEYS[config.unansweredPolicy]
    : undefined;
  const aggregationKey = config.subFieldAggregation
    ? SUB_FIELD_AGGREGATION_LABEL_KEYS[config.subFieldAggregation]
    : undefined;
  return (
    <div className="rounded-card border border-border-default bg-card p-5">
      <h2 className="mb-4 text-[16px] font-medium text-text-primary">
        {t("schema.scoringConfig.title")}
      </h2>
      <div className="grid grid-cols-3 gap-4">
        <div className="flex flex-col gap-0.5">
          <dt className="text-caption font-medium text-text-muted">
            {t("schema.scoringConfig.defaultPreset")}
          </dt>
          <dd className="font-mono text-body text-text-secondary">
            {config.defaultPresetKey ?? "-"}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-caption font-medium text-text-muted">
            {t("schema.scoringConfig.unansweredPolicy")}
          </dt>
          <dd className="text-body text-text-secondary">
            {policyKey ? t(policyKey) : config.unansweredPolicy ?? "-"}
          </dd>
        </div>
        <div className="flex flex-col gap-0.5">
          <dt className="text-caption font-medium text-text-muted">
            {t("schema.scoringConfig.subFieldAggregation")}
          </dt>
          <dd className="text-body text-text-secondary">
            {aggregationKey ? t(aggregationKey) : config.subFieldAggregation ?? "-"}
          </dd>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────

// ── Helper: group postures under domains client-side ──────────────────────────

function groupPosturesUnderDomains(
  domains: Domain[],
  postures: Posture[],
): DomainWithPostures[] {
  // Preferred path: posture.domainId is the source of truth. Falls back to a
  // question-cross-reference scan for legacy schemas whose postures predate
  // the domain FK.
  const domainPosturesById = new Map<string, Posture[]>();
  const unboundPostures: Posture[] = [];
  for (const p of postures) {
    if (p.domainId) {
      const arr = domainPosturesById.get(p.domainId) ?? [];
      arr.push(p);
      domainPosturesById.set(p.domainId, arr);
    } else {
      unboundPostures.push(p);
    }
  }

  // Legacy fallback: derive (domain, posture) pairs from questions.
  if (unboundPostures.length > 0) {
    const postureById = new Map(unboundPostures.map((p) => [p.id, p]));
    const seen = new Set<string>();
    for (const posture of unboundPostures) {
      for (const q of posture.questions) {
        const key = `${q.domainId}|${q.postureId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const p = postureById.get(q.postureId);
        if (!p) continue;
        const arr = domainPosturesById.get(q.domainId) ?? [];
        // Slice questions to those belonging to this domain.
        arr.push({
          ...p,
          questions: posture.questions.filter((qq) => qq.domainId === q.domainId),
        });
        domainPosturesById.set(q.domainId, arr);
      }
    }
  }

  return domains.map((domain) => {
    const domainPostures = (domainPosturesById.get(domain.id) ?? []).slice();
    domainPostures.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const questionCount = domainPostures.reduce(
      (sum, p) => sum + p.questions.length,
      0,
    );
    return { ...domain, postures: domainPostures, questionCount };
  });
}

export function SchemaDetail() {
  const { t, language } = useTranslation();
  // Route can be /schemas/:id/versions/:vId OR /schemas/:id
  const { id, vId } = useParams<{ id: string; vId?: string }>();
  const navigate = useNavigate();

  const [schema, setSchema] = useState<SchemaRecord | null>(null);
  const [schemaVersion, setSchemaVersion] = useState<SchemaVersionDetail | null>(null);
  const [allVersions, setAllVersions] = useState<
    Array<{ id: string; version: number; status: SchemaVersionStatus }>
  >([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string>(vId ?? "");
  const [isLoading, setIsLoading] = useState(true);
  const [versionsError, setVersionsError] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyName, setCopyName] = useState("");
  const [copyError, setCopyError] = useState<string | undefined>();
  const [isCopying, setIsCopying] = useState(false);
  // SCH-07 (S-171) review workflow state
  const [reviews, setReviews] = useState<VersionReviewRow[]>([]);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [isReviewing, setIsReviewing] = useState(false);
  const user = useAuthStore((s) => s.user);
  // Gate by the user's real CASL abilities (backend enforces; custom roles work).
  const canReview = userCan(user, "review", "SchemaVersion");
  const canPublish = userCan(user, "publish", "SchemaVersion");

  // Load parent schema (for name + type)
  useEffect(() => {
    if (!id) return;
    api
      .get<{ data: SchemaRecord }>(`/schemas/${id}`)
      .then((res) => setSchema(res.data ?? null))
      .catch(() => {});
  }, [id]);

  // Load versions list
  useEffect(() => {
    if (!id) return;
    api
      .get<{ data: Array<{ id: string; version: number; status: SchemaVersionStatus }> }>(
        `/schemas/${id}/versions`
      )
      .then((res) => {
        setVersionsError(false);
        setAllVersions(res.data ?? []);
        // If no vId in URL, pick the latest
        if (!vId) {
          if (res.data?.length) {
            const latest = res.data[0];
            if (latest) setSelectedVersionId(latest.id);
          } else {
            // No versions to load — the detail effect won't run, so release the
            // page spinner here to show the empty state instead of spinning forever.
            setIsLoading(false);
          }
        }
      })
      .catch(() => {
        setVersionsError(true);
        setAllVersions([]);
        // Without a version selected, the detail effect won't run and can't
        // clear the spinner — do it here so the error state can render.
        if (!vId) setIsLoading(false);
      });
  }, [id, vId]);

  // Load selected version detail
  useEffect(() => {
    if (!id || !selectedVersionId) return;
    let cancelled = false;
    setIsLoading(true);

    api
      .get<{ data: SchemaVersionDetail }>(
        `/schemas/${id}/versions/${selectedVersionId}`
      )
      .then((res) => {
        if (!cancelled) setSchemaVersion(res.data);
      })
      .catch(() => {
        if (!cancelled) setSchemaVersion(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id, selectedVersionId]);

  // SCH-07 (S-171): review history drives the approve/publish gating UI
  useEffect(() => {
    const status = schemaVersion?.status;
    if (!id || !selectedVersionId || (status !== "IN_REVIEW" && status !== "REJECTED")) {
      setReviews([]);
      return;
    }
    let cancelled = false;
    api
      .get<{ data: VersionReviewRow[] }>(`/schemas/${id}/versions/${selectedVersionId}/reviews`)
      .then((res) => {
        if (!cancelled) setReviews(res.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setReviews([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id, selectedVersionId, schemaVersion?.status]);

  async function handlePublish() {
    if (!id || !selectedVersionId) return;
    setIsPublishing(true);
    try {
      await api.post(`/schemas/${id}/versions/${selectedVersionId}/publish`);
      toast.success(t("schema.detail.publishedToastTitle"), t("schema.detail.publishedToastDesc"));
      setPublishOpen(false);
      // Reload
      const res = await api.get<{ data: SchemaVersionDetail }>(
        `/schemas/${id}/versions/${selectedVersionId}`
      );
      setSchemaVersion(res.data);
    } catch (err) {
      toast.error(
        t("schema.detail.publishFailedTitle"),
        err instanceof Error ? err.message : t("common.unknownError")
      );
    } finally {
      setIsPublishing(false);
    }
  }

  async function handleSubmitForReview() {
    if (!id || !selectedVersionId) return;
    try {
      await api.post(`/schemas/${id}/versions/${selectedVersionId}/submit`);
      toast.success(t("schema.detail.submittedToastTitle"), t("schema.detail.submittedToastDesc"));
      const res = await api.get<{ data: SchemaVersionDetail }>(
        `/schemas/${id}/versions/${selectedVersionId}`
      );
      setSchemaVersion(res.data);
    } catch (err) {
      toast.error(
        t("schema.detail.submitFailedTitle"),
        err instanceof Error ? err.message : t("common.unknownError")
      );
    }
  }

  // SCH-07 (S-171): record a review decision; rejection needs the dialog comment.
  async function handleReview(decision: "APPROVED" | "REJECTED") {
    if (!id || !selectedVersionId) return;
    setIsReviewing(true);
    try {
      await api.post(`/schemas/${id}/versions/${selectedVersionId}/review`, {
        decision,
        ...(rejectComment.trim() ? { comment: rejectComment.trim() } : {}),
      });
      toast.success(
        decision === "APPROVED"
          ? t("schema.review.approvedToastTitle")
          : t("schema.review.rejectedToastTitle"),
        decision === "APPROVED"
          ? t("schema.review.approvedToastDesc")
          : t("schema.review.rejectedToastDesc")
      );
      setRejectOpen(false);
      setRejectComment("");
      const res = await api.get<{ data: SchemaVersionDetail }>(
        `/schemas/${id}/versions/${selectedVersionId}`
      );
      setSchemaVersion(res.data);
    } catch (err) {
      toast.error(
        t("schema.review.reviewFailedTitle"),
        err instanceof Error ? err.message : t("common.unknownError")
      );
    } finally {
      setIsReviewing(false);
    }
  }

  async function handleRevertToDraft() {
    if (!id || !selectedVersionId) return;
    try {
      await api.post(`/schemas/${id}/versions/${selectedVersionId}/revert-to-draft`);
      toast.success(t("schema.review.revertedToastTitle"), t("schema.review.revertedToastDesc"));
      const res = await api.get<{ data: SchemaVersionDetail }>(
        `/schemas/${id}/versions/${selectedVersionId}`
      );
      setSchemaVersion(res.data);
    } catch (err) {
      toast.error(
        t("schema.review.revertFailedTitle"),
        err instanceof Error ? err.message : t("common.unknownError")
      );
    }
  }

  function openCopyDialog() {
    setCopyName(`${schema?.name ?? t("schema.detail.genericSchemaName")}${t("schema.detail.copyNameSuffix")}`);
    setCopyError(undefined);
    setCopyOpen(true);
  }

  // Deep-copy this schema into a new DRAFT — same flow as the Schemas list.
  async function handleCopy(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    if (!copyName.trim()) {
      setCopyError(t("schema.createDialog.nameRequired"));
      return;
    }
    setIsCopying(true);
    try {
      const res = await api.post<{ data: { id: string } }>(`/schemas/${id}/copy`, {
        name: copyName.trim(),
      });
      toast.success(t("schema.detail.copiedToastTitle"), `"${copyName.trim()}"${t("schema.detail.copiedToastDescSuffix")}`);
      setCopyOpen(false);
      const newId = res.data?.id ?? "";
      if (newId) navigate(`/schemas/${newId}`);
    } catch (err) {
      toast.error(t("schema.detail.copyFailedTitle"), err instanceof Error ? err.message : t("common.unknownError"));
    } finally {
      setIsCopying(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32 text-text-muted">
        {t("common.loading")}
      </div>
    );
  }

  if (!schemaVersion) {
    const fallbackMessage = versionsError
      ? t("schema.detail.loadVersionsError")
      : allVersions.length === 0
        ? t("schema.detail.noVersionsYet")
        : t("schema.detail.notFound");
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-32">
        <p className="text-body text-text-muted">{fallbackMessage}</p>
        <Button variant="outline" onClick={() => navigate("/schemas")}>
          {t("schema.detail.backToSchemas")}
        </Button>
      </div>
    );
  }

  const isDraft = schemaVersion.status === "DRAFT";
  const isInReview = schemaVersion.status === "IN_REVIEW";
  const isRejected = schemaVersion.status === "REJECTED";
  const isDeprecated = schemaVersion.status === "DEPRECATED";
  const isPreset = schema?.isPreset ?? false;
  const latestReview = reviews[0];

  // Derive domains-with-postures by grouping postures through their questions' domainId
  const domainsWithPostures = groupPosturesUnderDomains(
    schemaVersion.domains ?? [],
    schemaVersion.postures ?? [],
  );
  const questionColumns = buildQuestionColumns(t);

  return (
    <>
      {isDeprecated && (
        <ContextBanner
          variant="archived"
          title={t("status.deprecated")}
          message={t("schema.detail.deprecatedMessage")}
          dismissible={false}
        />
      )}

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <h1 className="text-[24px] font-semibold text-text-primary">
              {schema?.name ?? schemaVersion.schemaId}
            </h1>
            <StatusBadge type="schemaVersion" status={schemaVersion.status} />
            {isPreset && (
              <span className="inline-flex items-center rounded-badge bg-accent-bg px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-accent-text">
                {t("schema.detail.preloadedBadge")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-caption text-text-muted">
            <span className="font-mono">v{schemaVersion.version}</span>
            {schema?.type && (
              <span>{schema.type.replace(/_/g, " ")}</span>
            )}
            <span>
              {formatDate(schemaVersion.createdAt, language)}
            </span>
          </div>
        </div>

        {/* Context-sensitive actions */}
        <div className="flex items-center gap-2">
          {/* Version selector */}
          {allVersions.length > 1 && (
            <SelectRoot
              value={selectedVersionId}
              onValueChange={(v) => {
                setSelectedVersionId(v);
                navigate(`/schemas/${id}/versions/${v}`);
              }}
            >
              <SelectTrigger className="w-36">
                <SelectValue placeholder={t("schema.detail.selectVersionPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {allVersions.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    v{v.version}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          )}

          <Button
            variant="outline"
            leadingIcon={<Copy className="h-4 w-4" />}
            onClick={openCopyDialog}
          >
            {t("schema.detail.duplicate")}
          </Button>

          {isDraft && !isPreset && (
            <>
              <Button
                variant="outline"
                leadingIcon={<Edit className="h-4 w-4" />}
                onClick={() => navigate(`/schemas/${id}/versions/${selectedVersionId}/edit`)}
              >
                {t("schema.detail.editDraft")}
              </Button>
              <Button
                leadingIcon={<Send className="h-4 w-4" />}
                onClick={handleSubmitForReview}
              >
                {t("schema.detail.submitForReview")}
              </Button>
            </>
          )}

          {isInReview && !isPreset && (
            <>
              {canReview && (
                <>
                  <Button
                    variant="outline"
                    style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
                    onClick={() => setRejectOpen(true)}
                  >
                    {t("schema.review.reject")}
                  </Button>
                  <Button
                    variant="outline"
                    isLoading={isReviewing && !rejectOpen}
                    onClick={() => handleReview("APPROVED")}
                  >
                    {t("schema.review.approve")}
                  </Button>
                </>
              )}
              {canPublish && (
                <Button
                  leadingIcon={<CheckCircle2 className="h-4 w-4" />}
                  onClick={() => setPublishOpen(true)}
                  disabled={latestReview?.decision !== "APPROVED"}
                  title={
                    latestReview?.decision !== "APPROVED"
                      ? t("schema.review.publishNeedsApproval")
                      : undefined
                  }
                >
                  {t("schema.detail.publish")}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* SCH-07 (S-171): review state banners */}
      {!isPreset && isInReview && latestReview && (
        <div className="rounded-card border border-border-default bg-card px-4 py-3 text-body text-text-secondary">
          {latestReview.decision === "APPROVED"
            ? t("schema.review.awaitingPublish", {
                name: latestReview.reviewerName ?? latestReview.reviewerId,
              })
            : t("schema.review.rejectedBanner", {
                name: latestReview.reviewerName ?? latestReview.reviewerId,
              })}
          {latestReview.comment ? `: ${latestReview.comment}` : ""}
        </div>
      )}
      {!isPreset && isRejected && (
        <div
          className="flex items-center justify-between gap-4 rounded-card border bg-card px-4 py-3 text-body"
          style={{ borderColor: "var(--danger)", color: "var(--danger)" }}
        >
          <span>
            {t("schema.review.rejectedBanner", {
              name: latestReview?.reviewerName ?? latestReview?.reviewerId ?? "-",
            })}
            {latestReview?.comment ? `: ${latestReview.comment}` : ""}
          </span>
          {canPublish && (
            <Button variant="outline" size="sm" onClick={handleRevertToDraft}>
              {t("schema.review.revertToDraft")}
            </Button>
          )}
        </div>
      )}

      {/* Domains accordion */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h2 className="text-[16px] font-medium text-text-primary">
            {t("schema.domains")}
          </h2>
        </div>

        {domainsWithPostures.length === 0 ? (
          <div className="rounded-card border border-border-default bg-card py-12 text-center text-body text-text-muted">
            {t("schema.detail.noDomainsYet")}
          </div>
        ) : (
          <Accordion type="multiple" className="rounded-card border border-border-default bg-card">
            {domainsWithPostures.map((domain) => (
              <AccordionItem key={domain.id} value={domain.id}>
                <AccordionTrigger>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-caption text-text-muted">
                      {domain.code}
                    </span>
                    <span className="font-medium text-text-primary">
                      {domain.name}
                    </span>
                    <span className="text-caption text-text-muted">
                      {t("schema.detail.questionPlural", { count: domain.questionCount })}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    {domain.postures.length === 0 ? (
                      <p className="text-caption text-text-muted">
                        {t("schema.detail.noPosturesInDomain")}
                      </p>
                    ) : (
                      domain.postures.map((posture) => (
                        <div key={posture.id} className="flex flex-col gap-2">
                          <div className="flex items-center gap-3">
                            <span className="font-mono text-caption text-text-muted">
                              {posture.code}
                            </span>
                            <span className="font-medium text-text-secondary">
                              {posture.name}
                            </span>
                            {posture.weight !== null && posture.weight !== undefined && (
                              <span className="text-caption text-text-muted">
                                {t("schema.detail.weightLabel")} {posture.weight}
                              </span>
                            )}
                            <span className="text-caption text-text-muted">
                              {t("schema.detail.questionPlural", { count: posture.questions.length })}
                            </span>
                          </div>
                          {posture.questions.length > 0 && (
                            <DataTable
                              columns={questionColumns}
                              data={posture.questions}
                              className="ml-4"
                            />
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        )}
      </div>

      {/* Scoring Config */}
      <ScoringConfigCard config={schemaVersion.scoringConfig} />

      {/* Publish confirm */}
      {/* SCH-07 (S-171): reject dialog — comment is mandatory */}
      <Dialog
        open={rejectOpen}
        onOpenChange={(open) => {
          setRejectOpen(open);
          if (!open) setRejectComment("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("schema.review.rejectTitle")}</DialogTitle>
            <DialogDescription>{t("schema.review.rejectDesc")}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 py-2">
            <label className="text-caption text-text-secondary" htmlFor="schema-reject-comment">
              {t("schema.review.commentLabel")}
            </label>
            <Textarea
              id="schema-reject-comment"
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder={t("schema.review.commentPlaceholder")}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)} disabled={isReviewing}>
              {t("common.cancel")}
            </Button>
            <Button
              style={{ backgroundColor: "var(--danger)" }}
              isLoading={isReviewing}
              disabled={!rejectComment.trim()}
              onClick={() => handleReview("REJECTED")}
            >
              {t("schema.review.reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={publishOpen}
        onOpenChange={setPublishOpen}
        title={t("schema.detail.publishDialogTitle")}
        description={`${t("schema.detail.publishDialogDescPre")}v${schemaVersion.version}${t("schema.detail.publishDialogDescPost")}`}
        confirmLabel={t("schema.detail.publish")}
        isDestructive={false}
        isLoading={isPublishing}
        onConfirm={handlePublish}
      />

      {/* Duplicate schema */}
      <Dialog open={copyOpen} onOpenChange={(v) => { if (!isCopying) setCopyOpen(v); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("schema.detail.duplicateDialogTitle")}</DialogTitle>
            <DialogDescription>
              {t("schema.detail.duplicateDialogDescPre")}{schema?.name ?? t("schema.detail.thisSchema")}{t("schema.detail.duplicateDialogDescPost")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCopy} className="flex flex-col gap-4">
            <InputGroup
              label={t("schema.detail.newSchemaName")}
              required
              value={copyName}
              onChange={(e) => { setCopyName(e.target.value); setCopyError(undefined); }}
              placeholder={t("schema.detail.newSchemaNamePlaceholder")}
              errorText={copyError}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setCopyOpen(false)} disabled={isCopying}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" isLoading={isCopying}>
                {t("schema.detail.duplicate")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
